import type { StartConflict } from '@/types/scheduleExpectation';

const formatDate = (isoDate: string, timeZone: string) =>
  new Date(isoDate).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone
  });

function singleConflictMessage(conflict: StartConflict, timeZone: string): string {
  const isSequence = conflict.conflict_type === 'sequence';
  const noun = isSequence ? 'sequence' : 'expectation';
  const article = isSequence ? 'a' : 'an';

  if (conflict.action === 'delete' && conflict.reason === 'within_first_cycle') {
    return `There is already ${article} ${noun} for this appointment type on this date. We’ll need to delete the existing ${noun} to make room for this new one.`;
  }

  if (conflict.action === 'delete' && conflict.reason === 'not_yet_started') {
    const date = conflict.existing_start_at ? formatDate(conflict.existing_start_at, timeZone) : '';
    return `There is already ${article} ${noun} for this appointment type starting on ${date}. We’ll need to delete that ${noun} to make room for this new one.`;
  }

  const endDate = conflict.existing_end_at ? formatDate(conflict.existing_end_at, timeZone) : '';
  let message = `There is already ${article} ${noun} for this appointment type on this date. The last cycle for the existing ${noun} will end on ${endDate}.`;
  if (conflict.gap_days > 0) {
    message += ` Please be aware that there will be a ${conflict.gap_days} day${conflict.gap_days === 1 ? '' : 's'} gap between the end of that ${noun} and the start of this expectation.`;
  }
  return message;
}

/**
 * Builds one user-facing message per conflict a new expectation/sequence
 * start would create. There can be more than one -- e.g. a still-open
 * sequence and an orphaned standalone expectation for the same appointment
 * type -- so each conflict gets its own message, calling out whether it's an
 * expectation or a sequence being affected. Callers that need to render
 * multiple conflicts on separate lines should use this rather than joining
 * everything into a single string.
 */
export function startConflictMessages(conflicts: StartConflict[], timeZone: string): string[] {
  return conflicts.map((conflict) => singleConflictMessage(conflict, timeZone));
}

/**
 * Builds a single user-facing message describing every conflict a new
 * expectation/sequence start would create, joined into one string. Prefer
 * `startConflictMessages` when the messages will be displayed to a user, so
 * multiple conflicts can be shown on separate lines instead of run together.
 */
export function startConflictMessage(conflicts: StartConflict[], timeZone: string): string {
  return startConflictMessages(conflicts, timeZone).join(' ');
}
